import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'shared/api/queryKeys';
import { useWorkspacePicker } from 'workspaces/api';
import { DraftReferenceState } from 'shared/api/referenceOptions';
import { useKeycloakToken } from 'shared/auth/useKeycloak';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Route,
  NewIntegration,
  Organization,
  ProviderType,
  buildProviderType,
  buildNewIntegration,
} from 'shared/types/ObjectTypes';
import { createNewRoute } from 'routes/api/routesApi';
import NotFoundPage from 'shared/components/layout/NotFoundPage';
import { filterConnectionOwner } from 'connections/ConnectionHelperFunctions';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import NewRouteStepTwo from './NewRouteStepTwo';
import { loadProviderTypes } from 'routes/api/loadProviderTypesNewRoute';
import { useKeycloak } from '@react-keycloak/web';
import RouteProgressIndicator from './NewRouteProgressIndicator/RouteProgressIndicator';
import {
  trackRouteCreated,
  trackRouteCreationAbandoned,
} from 'shared/analytics/analytics';
import { loadCurrentUserDetails } from 'workspaces/loadCurrentUserDetails';
import { providerNeedsBaseUrl } from './providerBaseUrl';
import {
  ActionConfigErrors,
  validateActionConfigs,
  configurationsForCreate,
} from 'shared/components/forms/SchemaFormTemplates/validateActionConfigs';
import { routeSetupActions } from 'shared/components/forms/SchemaFormTemplates/actionFilters';
import { actionIsSentOnCreate } from 'shared/components/configPanel/actionSetup';
import { routeRecordId } from './routeDisplayName';
import { validateBaseUrl } from 'connections/validateBaseUrl';
import {
  constructEarthRangerUrl,
  isEarthRangerConnectionType,
} from 'connections/earthRangerUrl';
import { buildDraftReference } from 'shared/components/testAction/buildDraftReference';
import {
  buttonClasses,
  skeletonClasses,
  wizardClasses,
  buttonRowClasses,
} from 'styles/tailwindClassnames';
import { getRouteKind } from 'shared/utils/routeKind';

export function ManageAddRouteStepTwo() {
  type FilterTypes = {
    endpoint__in: FilterTags[];
    enabled: FilterTags[];
    type__in: FilterTags[];
    owner__in: FilterTags[];
    ordering: string;
    search: string;
  };
  function createFilterTypes(
    endpoint__in: FilterTags[],
    enabled: FilterTags[],
    type__in: FilterTags[],
    owner__in: FilterTags[],
    ordering: string,
    search: string,
  ): FilterTypes {
    return {
      endpoint__in: endpoint__in || [],
      enabled: enabled || [],
      type__in: type__in || [],
      owner__in: owner__in || [],
      ordering: ordering || '',
      search: search || '',
    };
  }
  type FilterTags = {
    name: string;
    id: string;
  };
  const { t } = useTranslation();
  const { pt_value } = useParams();
  const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
  const { keycloak, initialized } = useKeycloak();
  const [currentMemberIsAdmin, setCurrentMemberIsAdmin] =
    useState<boolean>(false);
  const [destinationFilter, setDestinationFilter] = useState<FilterTypes>(
    createFilterTypes([], [], [], [], '', ''),
  );
  const [initialRoute, setInitialRoute] = useState<NewIntegration>(
    buildNewIntegration(true),
  );
  const [createdRoute, setCreatedRoute] = useState<Route>();
  const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
  const [errors, setErrors] = useState({});
  // Keyed by action id: { [action_id]: { [field]: message } }.
  const [rsjfErrors, setRsjfErrors] = useState<ActionConfigErrors>({});
  const [routeId, setRouteId] = useState('');

  const isLoggedIn =
    !!keycloak?.authenticated &&
    localStorage.getItem('userAuthorized') !== null;
  const canFetch =
    isLoggedIn &&
    !!token &&
    typeof token === 'string' &&
    !keycloak.isTokenExpired();
  const { organizations, searchWorkspaces, clearWorkspaceSearch } =
    useWorkspacePicker({ enabled: canFetch });

  const [selectedProviderType, setSelectedProviderType] =
    useState<ProviderType>(buildProviderType());
  const [pageLoaded, setPageLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const navigate = useNavigate();

  const draftReference: DraftReferenceState | undefined = useMemo(
    () =>
      buildDraftReference({
        type: selectedProviderType,
        owner: initialRoute.owner,
        baseUrl: initialRoute.base_url,
        updatedConfigurations,
      }),
    [
      selectedProviderType,
      initialRoute.owner,
      initialRoute.base_url,
      updatedConfigurations,
    ],
  );

  // The technology named in the URL. No match (e.g. a stale bookmark) is a 404.
  const providerTypeQuery = useQuery({
    queryKey: queryKeys.newRouteProviderType(pt_value ?? ''),
    enabled: canFetch && !!pt_value,
    queryFn: async () => {
      const [provider_types, error] = await loadProviderTypes(
        token as string,
        pt_value as string,
      );
      if (error) throw error;
      return provider_types[0] ?? null;
    },
  });
  useEffect(() => {
    const providerType = providerTypeQuery.data;
    if (providerType === undefined) return;
    if (providerType === null) {
      setNotFound(true);
      return;
    }
    setNotFound(false);
    setSelectedProviderType(providerType);
    setInitialRoute((prevRoute: any) => ({
      ...prevRoute,
      type: providerType.id,
    }));
    setPageLoaded(true);
  }, [providerTypeQuery.data]);

  const handleChangeEndpoint = (e: any) => {
    setInitialRoute((prevRoute: any) => ({
      ...prevRoute,
      base_url: e.target.value,
    }));
  };

  const handleChangeSchemaForm = (e: any, action_id: string) => {
    if (!e) return;
    const newConfig = { action: action_id, data: e };
    // One array, built once and used for both states. A functional update on one
    // and a closure read of stale updatedConfigurations on the other drops a
    // section edited exactly once from the POST, and mutating in place hands
    // React the same reference so nothing re-renders. Same fix as GUNDI-5660
    // on ManageConnectionModal.
    const index_to_find = updatedConfigurations.findIndex(
      (a: any) => a.action === action_id,
    );
    const next =
      index_to_find === -1
        ? [...updatedConfigurations, newConfig]
        : updatedConfigurations.map((c: any, i: number) =>
            i === index_to_find ? newConfig : c,
          );
    setUpdatedConfigurations(next);
    setInitialRoute((prevRoute: any) => ({
      ...prevRoute,
      configurations: next,
    }));
  };

  const handleChangeName = (e: any) => {
    setInitialRoute((prevRoute: any) => ({
      ...prevRoute,
      name: e.target.value,
    }));
  };

  const handleSelectOwner = async (e: any) => {
    // Check if e and e.value are defined
    if (!e || !e.value) {
      return;
    }

    // Check if user is authenticated before making API calls
    const isLoggedIn =
      keycloak?.authenticated &&
      localStorage.getItem('userAuthorized') !== null;
    if (!isLoggedIn) {
      return;
    }

    try {
      const isAdmin = await loadCurrentUserDetails(
        e.value,
        keycloak.token ? keycloak.token : '',
      );
      setCurrentMemberIsAdmin(isAdmin);
      setInitialRoute((prevRoute: any) => ({
        ...prevRoute,
        owner: e.value,
      }));
      setDestinationFilter(
        filterConnectionOwner(
          {
            target: {
              name: e.label || '',
              value: e.value,
            },
          },
          destinationFilter,
        ),
      );
    } catch (err) {
      // Silently handle errors - they're already handled by the interceptor
      console.log('Failed to load user details:', err);
    }
  };

  // The sections whose required fields count: everything the screen shows, minus
  // anything switched off. Same predicate the payload is built from, so the
  // screen validates exactly what it sends.
  const enabledSetupActions = () =>
    routeSetupActions(selectedProviderType?.actions).filter((action: any) =>
      actionIsSentOnCreate(
        action,
        updatedConfigurations.find((c: any) => c.action === action.id)?.data,
      ),
    );

  const formIsValid = async () => {
    let form_errors: any = {};
    if (initialRoute.name.length === 0)
      form_errors.name = t('Name is required.');
    if (initialRoute.name.length > 50)
      form_errors.name = t('Name cannot exceed 50 characters.');
    // Gundi cannot reach these technologies without an address, so the
    // Route can't be created without one.
    const needsBaseUrl = providerNeedsBaseUrl(selectedProviderType?.value);
    const isEarthRanger = isEarthRangerConnectionType(
      selectedProviderType?.name || '',
    );
    // Shared validator: this copy used to be the untranslated one, and said
    // "url" where the other screens said "URL".
    const urlError = validateBaseUrl({
      value: initialRoute.base_url,
      asSiteName: isEarthRanger,
      optional: !needsBaseUrl,
      t,
    });
    if (urlError) form_errors.base_url = urlError;
    if (initialRoute.owner === '') form_errors.owner = t('Select a workspace.');

    setErrors(form_errors);

    // Every section the screen shows, not just Credentials: they are all on
    // the page, so an empty required field is visible work the user owes. Left
    // unvalidated, it reaches the API as a defaults-only configuration and the
    // whole Route is rejected with no field to point at.
    // A section switched off by its own toggle is inert — the connector never
    // runs it — so its required fields are not work the user owes.
    const rsjf_errors = validateActionConfigs(
      enabledSetupActions(),
      updatedConfigurations,
      t,
    );
    setRsjfErrors({ ...rsjf_errors });
    return (
      Object.keys(form_errors).length === 0 &&
      Object.keys(rsjf_errors).length === 0
    );
  };

  const handleCreateRoute = async (routeToAdd: NewIntegration) => {
    if (!(await formIsValid())) {
      // The blocking fields can sit in a section the user never opened, and
      // nothing relocates them — same toast as the New Connection screen.
      toast.error(
        t(
          'There are fields that require your attention. Please check and correct the errors.',
        ),
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }

    // Check if user is authenticated before making API calls
    const isLoggedIn =
      keycloak?.authenticated &&
      localStorage.getItem('userAuthorized') !== null;
    if (!isLoggedIn) {
      return;
    }

    if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
      try {
        // File-widget values go to the backend as JSON strings; done here,
        // on the payload, rather than by rewriting the form's state.
        // Switched-off sections are left out — see configurationsForCreate.
        const temp = await createNewRoute(token, {
          ...routeToAdd,
          configurations: configurationsForCreate(
            selectedProviderType?.actions,
            routeToAdd.configurations ?? [],
          ),
        });
        // The record can arrive as default_route or, on some responses, only
        // in routing_rules — routeRecordId knows both. Bailing on the
        // narrower check left a created Route with no toast and no
        // navigation, so the user pressed Create again and made a duplicate.
        const createdRecordId = temp ? routeRecordId(temp) : undefined;
        if (!temp || !createdRecordId) {
          toast.error(
            `${t('Something went wrong. Please try again or contact support.')}`,
            {
              position: toast.POSITION.TOP_RIGHT,
            },
          );
          return;
        }
        setCreatedRoute(temp);
        setRouteId(createdRecordId);
        const workspaceName =
          temp.owner?.name ||
          organizations.find((o: Organization) => o.id === initialRoute.owner)
            ?.name;
        trackRouteCreated(temp.id, selectedProviderType?.name, workspaceName);
        toast.success(`${t('New Route made, ready to add destinations')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        // Destinations are added on the flow map now — land there with the
        // "+ Add destination" button highlighted instead of a third step.
        // `temp.id` is the provider integration the POST created, which is
        // what route URLs address; createdRecordId above still gates on the
        // route record existing, since without one there is no Route page.
        navigate(`/routes/${temp.id}`, {
          state: { highlightAddDestination: true },
        });
      } catch (err: any) {
        console.log('Failed to create route:', err);
        // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
        if (err?.response?.status === 401) return;
        toast.error(
          t('Something went wrong. Please try again or contact support.'),
          {
            position: toast.POSITION.TOP_RIGHT,
          },
        );
      }
    }
  };

  const handleClickNext = async (newStep: number) => {
    if (newStep === 3) {
      if (!(await formIsValid())) {
        setCurrentStep(currentStep);
        return;
      }
      // On success this navigates to the Route's flow map — the wizard
      // ends here, so there is no step 3 to advance to.
      // EarthRanger sites are typed as a site name; the record stores the URL.
      await handleCreateRoute(
        isEarthRangerConnectionType(selectedProviderType?.name || '')
          ? {
              ...initialRoute,
              base_url: constructEarthRangerUrl(initialRoute.base_url),
            }
          : initialRoute,
      );
    }
  };

  function handleSearchOrgDropdown(e: any) {
    searchWorkspaces(e);
  }

  const handleLoadMoreOrganizations = () => {
    clearWorkspaceSearch();
  };

  const handleCancel = () => {
    const workspaceName = organizations.find(
      (o: Organization) => o.id === initialRoute.owner,
    )?.name;
    trackRouteCreationAbandoned(2, selectedProviderType?.name, workspaceName);
    navigate('/');
  };

  if (notFound) return <NotFoundPage />;

  return !pageLoaded ? (
    <div>
      <div className={wizardClasses.band}>
        <div className={wizardClasses.row}>
          <h1 className={wizardClasses.title}>
            {t('New')} {t('Route')}
          </h1>
        </div>
      </div>
      <div className={wizardClasses.body}>
        <div className={wizardClasses.row}>
          {/* The provider type hasn't loaded yet, but the webhook kind is
                        derivable from the URL slug — avoids a 4-to-5-step layout shift */}
          <RouteProgressIndicator
            stepZeroStatus="completed"
            stepOneStatus="in-progress"
            stepTwoStatus="upcoming"
            webhookStepStatus={
              pt_value === 'generic_webhooks' ? 'upcoming' : undefined
            }
          />
          <div className="mt-8 w-full rounded p-[16px]">
            <div className={`h-5 w-96 max-w-full ${skeletonClasses.base}`} />
            <div className="mt-6 flex flex-row">
              <div className="mr-3 flex w-1/2 flex-col gap-2">
                <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                <div className={`h-10 w-full ${skeletonClasses.base}`} />
              </div>
              <div className="mr-3 flex w-1/2 flex-col gap-2">
                <div className={`h-4 w-32 ${skeletonClasses.base}`} />
                <div className={`h-10 w-full ${skeletonClasses.base}`} />
              </div>
            </div>
            <div className="mt-8 flex flex-col gap-2">
              <div className={`h-5 w-80 max-w-full ${skeletonClasses.base}`} />
              <div className={`h-10 w-full ${skeletonClasses.base}`} />
              <div className={`h-10 w-full ${skeletonClasses.base}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div>
      <div className={wizardClasses.band}>
        <div className={wizardClasses.row}>
          <h1 className={wizardClasses.title}>
            {t('New')} {selectedProviderType?.name || ''} {t('Route')}
          </h1>
        </div>
      </div>
      <div className={wizardClasses.body}>
        <div className={wizardClasses.row}>
          <RouteProgressIndicator
            stepZeroStatus="completed"
            stepOneStatus="in-progress"
            stepTwoStatus="upcoming"
            webhookStepStatus={
              getRouteKind(selectedProviderType) === 'webhook'
                ? 'upcoming'
                : undefined
            }
          />
          <NewRouteStepTwo
            currentMemberIsAdmin={currentMemberIsAdmin}
            initialRoute={initialRoute}
            errors={errors}
            rsjfErrors={rsjfErrors}
            onChangeSchemaForm={handleChangeSchemaForm}
            configurations={updatedConfigurations}
            onChangeName={handleChangeName}
            onChangeEndpoint={handleChangeEndpoint}
            onSelectOwner={handleSelectOwner}
            selectedProviderType={selectedProviderType}
            organizations={organizations}
            loadMoreOrgs={handleLoadMoreOrganizations}
            orgInputChange={handleSearchOrgDropdown}
            draftReference={draftReference}
            authToken={typeof token === 'string' ? token : undefined}
          />
        </div>
      </div>

      <div className={wizardClasses.footer}>
        <div className="px-4 md:px-8 desktop:px-[60px]">
          <div className={wizardClasses.row}>
            <div className={buttonRowClasses.row}>
              <button
                className={buttonClasses.primary}
                onClick={() => handleClickNext(currentStep + 1)}
                disabled={!initialRoute.name.trim() || !initialRoute.owner}
                data-testid="ManageAddRouteStepTwo-next-button"
              >
                {t('Create Route')}
              </button>
              <button
                className={buttonClasses.cancel}
                type="button"
                onClick={handleCancel}
                data-testid="ManageAddRouteStepTwo-cancel-button"
              >
                {t('Cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
